// Activation QR for a pending person invite. Fetches the current activation link
// (backend silently re-mints if expired) and renders it as a scannable QR.
// Viewable any time until the account is activated.
import React, { useEffect, useState } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Loader2, Download, Copy, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { formatDT } from '../../utils/timezone';

export const PersonInviteQrModal = ({ open, onClose, api, person, personType }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !person?.id) return;
    let cancelled = false;
    setLoading(true); setData(null); setError('');
    api.get(`/people/${personType}/${person.id}/invite-qr`)
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.detail || 'Failed to load activation QR'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, person?.id, personType, api]);

  const downloadPng = () => {
    const canvas = document.querySelector('#person-invite-qr-canvas canvas');
    if (!canvas) return;
    const link = document.createElement('a');
    link.href = canvas.toDataURL('image/png');
    link.download = `activation-qr-${(person?.full_name || 'person').replace(/\s+/g, '-').toLowerCase()}.png`;
    link.click();
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(data.invite_url); toast.success('Activation link copied'); }
    catch { toast.error('Could not copy link'); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-sm" data-testid="person-invite-qr-modal">
        <DialogHeader>
          <DialogTitle>Account Activation QR</DialogTitle>
          <DialogDescription>
            {person?.full_name} can scan this to set their password and activate their login.
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="flex items-center justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
        ) : error ? (
          <div className="flex items-start gap-2 rounded-md border border-amber-300/60 bg-amber-50 p-3 text-xs text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300" data-testid="person-invite-qr-error">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /> {error}
          </div>
        ) : data ? (
          <div className="flex flex-col items-center gap-3" data-testid="person-invite-qr-panel">
            <div id="person-invite-qr-canvas" className="rounded-lg border bg-white p-3">
              <QRCodeCanvas value={data.invite_url} size={220} level="M" includeMargin={false} />
            </div>
            {data.login_email && <p className="text-xs text-muted-foreground font-mono">{data.login_email}</p>}
            <p className="text-[11px] text-muted-foreground text-center">
              Link valid until <span className="font-medium text-foreground">{data.expires_at ? formatDT(data.expires_at) : '—'}</span>.
              Reopening after expiry issues a fresh code automatically. This QR disappears once the account is activated.
            </p>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={downloadPng} data-testid="person-invite-qr-download"><Download className="w-3.5 h-3.5 mr-1.5" /> Download PNG</Button>
              <Button size="sm" variant="outline" onClick={copyLink} data-testid="person-invite-qr-copy"><Copy className="w-3.5 h-3.5 mr-1.5" /> Copy link</Button>
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export default PersonInviteQrModal;
